<template>
  <modal size="md" content-class="p-sm-4" @close="close">
    <template #header>
      <h5 class="mb-0">Pay Invoice</h5>
    </template>

    <template #body>
      <p v-if="invoice.total" class="text-center mb-3">
        Amount to pay: <strong>${{ invoice.total }}</strong>
      </p>
      <label class="d-block mb-1 small text-muted">Card details</label>
      <div ref="cardEl" class="form-control stripe-form__element" />
      <div v-if="errorMessage" class="text-danger small mt-2">{{ errorMessage }}</div>
    </template>

    <template #footer>
      <app-button variant="outline-primary" :disabled="submitting" @click="close">Cancel</app-button>
      <app-button :loading="submitting" :disabled="!ready" @click="submit">Pay Now</app-button>
    </template>
  </modal>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import { loadStripe } from '@stripe/stripe-js'
import Modal from '@/components/Modal'
import AppButton from '@/components/AppButton'
import invoicesApi from '@/services/api/invoices'
import EventBus from '@/store/EventBus'

export default defineComponent({
  name: 'InvoiceStripeForm',
  components: { Modal, AppButton },
  props: {
    invoice: { type: Object, required: true }
  },
  emits: ['close', 'paid'],
  setup(props, { emit }) {
    const { proxy } = getCurrentInstance()
    const cardEl = ref(null)
    const ready = ref(false)
    const submitting = ref(false)
    const errorMessage = ref('')
    let stripe = null
    let card = null
    let clientSecret = null

    onMounted(async () => {
      try {
        const [stripeInstance, secretRes] = await Promise.all([
          loadStripe(proxy.$stripePk),
          invoicesApi.getStripeSecret(props.invoice.id)
        ])
        stripe = stripeInstance
        clientSecret = secretRes.data.data.client_secret
        const elements = stripe.elements()
        card = elements.create('card', { hidePostalCode: true })
        card.mount(cardEl.value)
        card.on('change', (event) => {
          errorMessage.value = event.error != null ? event.error.message : ''
        })
        ready.value = true
      } catch (_err) {
        errorMessage.value = 'Unable to load the payment form. Please try again.'
      }
    })

    onBeforeUnmount(() => {
      if (card != null) card.destroy()
    })

    async function submit() {
      if (stripe == null || card == null || clientSecret == null || submitting.value) return
      submitting.value = true
      errorMessage.value = ''
      try {
        const { error } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card }
        })
        if (error != null) {
          errorMessage.value = error.type === 'card_error' || error.type === 'validation_error'
            ? error.message
            : 'An unexpected error occurred.'
          return
        }
        const res = await invoicesApi.confirmPayment(props.invoice.id, {})
        EventBus.$emit('show-success', res.data.message != null ? res.data.message : 'Payment completed successfully.')
        emit('paid')
      } catch (err) {
        const data = err.response != null ? err.response.data : null
        errorMessage.value = data != null && data.message != null ? data.message : 'An unexpected error occurred.'
      } finally {
        submitting.value = false
      }
    }

    function close(event) {
      if (event != null && typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      if (submitting.value) return
      emit('close')
    }

    return {
      cardEl,
      ready,
      submitting,
      errorMessage,
      submit,
      close
    }
  }
})
</script>

<style scoped>
.stripe-form__element {
  height: auto;
  padding-top: 12px;
  padding-bottom: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/Invoices/InvoiceStripeForm.vue